import React from 'react';
import PropTypes from 'prop-types';
import { useReducer } from 'reinspect';

const LayoutStateContext = React.createContext();
const LayoutActionsContext = React.createContext();

export function useLayoutState() {
  return React.useContext(LayoutStateContext);
}

const initialState = {
  dialogVisible: false,
  fullscreen: false,
  timerPeriod: 3,
  certification: {
    timer: {
      begin: false,
      progress: false,
      end: false,
      start: false,
      stop: false,
    },
  },
};

export const types = {
  TOGGLE_DIALOG_VISIBLE: 'TOGGLE_DIALOG_VISIBLE',
  ON_CERTIFICATION_TIMER_START: 'ON_CERTIFICATION_TIMER_START',
  ON_CERTIFICATION_TIMER_STOP: 'ON_CERTIFICATION_TIMER_STOP',
  ON_CERTIFICATION_TIMER_RESET: 'ON_CERTIFICATION_TIMER_RESET',
  ON_CERTIFICATION_TIMER_BEGIN: 'ON_CERTIFICATION_TIMER_BEGIN',
  ON_CERTIFICATION_TIMER_PROGRESS: 'ON_CERTIFICATION_TIMER_PROGRESS',
  ON_CERTIFICATION_TIMER_END: 'ON_CERTIFICATION_TIMER_END',
  UPDATE_PROP: 'UPDATE_PROP',
  TOGGLE_FULLSCREEN: 'TOGGLE_FULLSCREEN',
  TURN_OFF_FULLSCREEN: 'TURN_OFF_FULLSCREEN',
};

export function useLayoutActions() {
  const { dispatch } = React.useContext(LayoutActionsContext);
  function onStart() {
    dispatch({
      type: types.ON_CERTIFICATION_TIMER_START,
    });
  }
  function onStop() {
    dispatch({
      type: types.ON_CERTIFICATION_TIMER_STOP,
    });
  }
  function onEnd() {
    dispatch({
      type: types.ON_CERTIFICATION_TIMER_END,
    });
  }

  function onReset() {
    dispatch({
      type: types.ON_CERTIFICATION_TIMER_RESET,
    });
  }
  function onProgress() {
    dispatch({
      type: types.ON_CERTIFICATION_TIMER_PROGRESS,
    });
  }
  function updateProp(key, value) {
    dispatch({
      type: types.UPDATE_PROP,
      meta: key,
      payload: value,
    });
  }
  function toggleDialogVisible() {
    dispatch({
      type: types.TOGGLE_DIALOG_VISIBLE,
    });
  }
  function toggleFullscreen() {
    dispatch({
      type: types.TOGGLE_FULLSCREEN,
    });
  }
  function turnOffFullscreen() {
    dispatch({
      type: types.TURN_OFF_FULLSCREEN,
    });
  }
  return {
    onStart,
    onEnd,
    onStop,
    onReset,
    onProgress,
    updateProp,
    toggleDialogVisible,
    toggleFullscreen,
    turnOffFullscreen,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case types.ON_CERTIFICATION_TIMER_START:
      return {
        ...state,
        certification: {
          timer: {
            start: true,
            stop: false,
            begin: false,
            end: false,
            progress: false,
          },
        },
      };
    case types.ON_CERTIFICATION_TIMER_STOP:
      return {
        ...state,
        certification: {
          timer: {
            start: false,
            stop: true,
            begin: false,
            end: false,
            progress: false,
          },
        },
      };
    case types.ON_CERTIFICATION_TIMER_END:
      return {
        ...state,
        certification: {
          timer: {
            start: false,
            stop: false,
            begin: false,
            end: true,
            progress: false,
          },
        },
      };
    case types.ON_CERTIFICATION_TIMER_PROGRESS:
      return {
        ...state,
        certification: {
          timer: {
            start: false,
            stop: false,
            begin: true,
            end: false,
            progress: true,
          },
        },
      };
    case types.ON_CERTIFICATION_TIMER_RESET:
      return {
        ...state,
        certification: {
          timer: {
            start: false,
            stop: false,
            begin: false,
            end: false,
            progress: false,
          },
        },
      };
    case types.TOGGLE_DIALOG_VISIBLE:
      return {
        ...state,
        dialogVisible: !state.dialogVisible,
      };
    case types.TOGGLE_FULLSCREEN:
      return {
        ...state,
        fullscreen: !state.fullscreen,
      };
    case types.TURN_OFF_FULLSCREEN:
      return {
        ...state,
        fullscreen: false,
      };
    case types.UPDATE_PROP:
      return {
        ...state,
        [action.meta]: action.payload,
      };

    default:
      return state;
  }
}

export function LayoutContextProvider({ children }) {
  const [state, dispatch] = useReducer(
    reducer,
    initialState,
    undefined,
    'LAYOUT'
  );

  const actions = React.useMemo(
    () => ({
      dispatch,
    }),
    []
  );

  return (
    <LayoutStateContext.Provider value={state}>
      <LayoutActionsContext.Provider value={actions}>
        {children}
      </LayoutActionsContext.Provider>
    </LayoutStateContext.Provider>
  );
}

LayoutContextProvider.propTypes = {
  children: PropTypes.array.isRequired,
};
